import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { stopEvent } from '../../helper/elements';

type ComboBoxType = {
  placeholder: string;
  type?: string;
  actionLabel: string;
  searchPlaceholder: string;
  subActionLabel?: string;
  onItemChoose(item: any): void;
  options: any[];
  value: any;
  onAction?(value: any): void;
  onSubAction?(value: any): void;
  error?: boolean;
  disabled?: boolean;
  id?: string;
  myShopItems?: boolean;
  myShopItemsAction?(): void;
  twoRowDisplay?: boolean;
};

export function Combobox(props: ComboBoxType) {
  const {
    placeholder = 'Select...',
    type = '',
    actionLabel = 'Action label',
    searchPlaceholder = 'Type to search...',
    subActionLabel = '',
    onItemChoose,
    options = [],
    value,
    onAction,
    onSubAction,
    error = false,
    disabled = false,
    id = '',
    myShopItems = false,
    myShopItemsAction,
    twoRowDisplay = false,
  } = props;

  const [inputValue, setInputValue] = React.useState('');
  const [open, setOpen] = React.useState(false);

  const handleInputValue = (value: string) => {
    setInputValue(value);
  };

  const foundSearchedItem = options?.find(
    (f) =>
      f?.name?.toLowerCase() === inputValue?.toLowerCase() ||
      f?.displayName?.toLowerCase() === inputValue?.toLowerCase()
  );

  const searchKeys = [
    'name',
    'displayName',
    'value',
    'pattern',
    'supplier',
    'firstName',
    'lastName',
    'itemNumber',
  ];

  const optionsById = new Map<number, any>(options.map((item) => [item.id, item]));

  const matchesSearch = (item: any, search: string) =>
    searchKeys.some((key) => item[key]?.toString().toLowerCase().includes(search.toLowerCase()));

  const collectAllSubClients = (parentId: number, acc = new Map<number, any>()) => {
    const parent = optionsById.get(parentId);
    if (!parent?.subClientsIds?.length) return acc;

    parent.subClientsIds.forEach((id: number) => {
      if (!acc.has(id)) {
        const child = optionsById.get(id);
        if (child) {
          acc.set(id, child);
          collectAllSubClients(id, acc); // recurse
        }
      }
    });

    return acc;
  };

  const filteredOptions = React.useMemo(() => {
    if (!inputValue) return options;

    const matchedItems = options.filter((item) => matchesSearch(item, inputValue));

    const resultMap = new Map<number, any>();

    matchedItems.forEach((item) => {
      resultMap.set(item.id, item);

      // collect ALL descendants
      const descendants = collectAllSubClients(item.id);
      descendants.forEach((value, key) => {
        resultMap.set(key, value);
      });
    });

    return Array.from(resultMap.values());
  }, [options, inputValue]);

  const handleEditItem = (e: any) => {
    stopEvent(e);

    if (myShopItemsAction) {
      myShopItemsAction();
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          disabled={disabled}
          variant={error ? 'error' : 'outline'}
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between', twoRowDisplay ? 'max-height-[56px] h-full' : '')}
        >
          <span
            className={cn(
              twoRowDisplay ? 'line-clamp-2 text-left whitespace-pre-line' : 'truncate'
            )}
          >
            {value ? value : placeholder}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {myShopItems && (
              <span
                onPointerDown={handleEditItem}
                className="text-xs text-primary hover:underline cursor-pointer"
              >
                Edit
              </span>
            )}
            <ChevronsUpDown className="opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0">
        <Command>
          <CommandInput
            onValueChange={(value) => handleInputValue(value)}
            placeholder={searchPlaceholder}
            value={inputValue}
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((item) => (
                <CommandItem
                  key={item.id}
                  value={JSON.stringify(item)}
                  onSelect={(currentValue) => {
                    const parsedObj = JSON.parse(currentValue);

                    onItemChoose(parsedObj);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      '',
                      value === item.displayName ||
                        value === item.value ||
                        value === item.name ||
                        item.pattern ||
                        item.supplier
                        ? 'opacity-100'
                        : 'opacity-0'
                    )}
                  />
                  {item?.parent && `${item?.parent?.displayName} | `}
                  {item.displayName ||
                    item.value ||
                    item.name ||
                    item.pattern ||
                    item.supplier ||
                    item.firstName ||
                    item.lastName ||
                    ''}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>

          {type &&
            (type === 'action' ? (
              <div className="w-full p-2 border-t border-input">
                <div className="w-full">
                  <Button
                    onClick={() => {
                      if (onAction) onAction(inputValue);
                      setOpen(false);
                    }}
                    className="w-full py-0 min-h-[36px]"
                    variant={'default'}
                  >
                    {actionLabel}
                  </Button>
                </div>
              </div>
            ) : (
              // Remove true when implement the dynamic value prop
              inputValue &&
              !foundSearchedItem && (
                <>
                  <div className="text-[0.75rem] text-muted-foreground font-medium py-[0.375rem] px-[0.5rem]">
                    Create: "{inputValue}"
                  </div>

                  <div className="w-full p-2">
                    <div className="w-full">
                      <Button
                        onClick={() => {
                          if (onAction) onAction(inputValue);
                          setOpen(false);
                        }}
                        className="w-full py-0 min-h-[36px]"
                        variant={'default'}
                      >
                        {actionLabel}
                      </Button>
                    </div>
                    {subActionLabel && (
                      <div className="w-full">
                        <Button
                          onClick={() => {
                            if (onSubAction) onSubAction(inputValue);
                            setOpen(false);
                          }}
                          className="mt-2 w-full py-0 min-h-[36px]"
                          variant={'outline'}
                        >
                          {subActionLabel}
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )
            ))}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
